import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { acknowledgeEtlHalt, getEtlHaltCapacity, getEtlHalts } from '../services/etlHaltsApi'
import { errorKind } from '../utils/format'
import { FREQUENT } from '../utils/polling'

/**
 * ETL halts, on FREQUENT: a halt is a health state that blocks every run until someone clears it, so a
 * surface showing one should notice it being cleared (or a second one landing) without a reload.
 *
 * `includeAcknowledged` is part of the key by value, so the blocking-only list and the history are separate
 * cache entries and switching between them never serves the other's rows.
 */
export function useEtlHalts(includeAcknowledged: boolean) {
  return useQuery({
    queryKey: ['etl-halts', includeAcknowledged],
    queryFn: () => getEtlHalts(includeAcknowledged),
    refetchInterval: FREQUENT,
  })
}

/**
 * The storage headroom readout, for the clear-halt dialog only — fetched when `enabled` (the dialog is open).
 *
 * staleTime 0 and no polling: it is read fresh each time the dialog opens, because the question is "is there
 * room NOW", and it is not a live gauge. A 403 is an answer (not an administrator), so it is not retried.
 */
export function useEtlHaltCapacity(enabled: boolean) {
  return useQuery({
    queryKey: ['etl-halt-capacity'],
    queryFn: getEtlHaltCapacity,
    enabled,
    staleTime: 0,
    retry: (failureCount, error) => errorKind(error) !== 'forbidden' && failureCount < 3,
  })
}

/**
 * Every query a clear can change, as prefixes. `['observatory', 'signals']` belongs to the signal store, whose
 * hooks may not be mounted (or exist yet): invalidating a key nothing holds is a no-op.
 */
export function clearInvalidationKeys(runId: string): readonly (readonly unknown[])[] {
  return [
    ['etl-halts'],
    ['etl-job-catalog'],
    ['etl-halt-capacity'],
    ['observatory', 'executions'],
    ['observatory', 'execution', runId],
    ['observatory', 'signals'],
  ]
}

/**
 * Clears a halt (bearer-only; a note is required by the server).
 *
 * On success it invalidates every query the clear can change — the halt list and history, the catalogue (whose
 * halt summary gates the Run buttons), the capacity readout, Execution History, and the signal store — and says
 * nothing itself: the dialog shows the toast from the response. It never claims a signal resolved; the detector
 * decides that on its own schedule, and the invalidated read will show whatever it decided.
 */
export function useAcknowledgeEtlHalt() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ runId, note }: { runId: string; note: string }) => acknowledgeEtlHalt(runId, note),
    onSuccess: (_response, { runId }) => {
      for (const queryKey of clearInvalidationKeys(runId)) {
        void queryClient.invalidateQueries({ queryKey })
      }
    },
  })
}
